import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  TextField,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormHelperText,
  Checkbox,
  Typography,
  useTheme,
  InputAdornment,
  Button,
} from "@mui/material";
import IOSSwitch from "../../../components/IOSSwitch";
import { Visibility, VisibilityOff } from "@mui/icons-material";

interface CredentialFormProps {
  selectedCredential: string;
  username: string;
  usernameError: string;
  handleUsernameChange: (e: any) => void;
  passwordType: string;
  passwordTypeError: string;
  handlePasswordTypeChange: (e: any) => void;
  password: string;
  passwordError: string;
  handlePasswordChange: (e: any) => void;
  execPassword: boolean;
  handleExecPasswordToggle: (e: any) => void;
  autoAssign: boolean;
  handleAutoAssignToggle: (e: any) => void;
  execPasswordText: string;
  handleExecPasswordText: (e: any) => void;
  saveCredential: boolean;
  handleSaveCredential: (e: any) => void;
  isScheduled: boolean;
  credName: string;
  credNameError: string;
  handleCredName: (e: any) => void;
  component?: string
  selectedVendor?: any;
  vendorName?: string;
  onPrivateKeyUpload?: (file: File | null, content?: string) => void;
  privateKeyValidationMessage?: string;
}

const CredentialForm: React.FC<CredentialFormProps> = ({
  selectedCredential,
  username,
  usernameError,
  handleUsernameChange,
  passwordType,
  passwordTypeError,
  handlePasswordTypeChange,
  password,
  passwordError,
  handlePasswordChange,
  execPassword,
  handleExecPasswordToggle,
  autoAssign,
  handleAutoAssignToggle,
  execPasswordText,
  handleExecPasswordText,
  saveCredential,
  handleSaveCredential,
  isScheduled,
  credName,
  credNameError,
  handleCredName,
  component,
  vendorName,
  onPrivateKeyUpload,
  privateKeyValidationMessage
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [seePassword, setSeePassword] = useState(false);
  const [privateKeyFileName, setPrivateKeyFileName] = useState("");
  const [privateKeyError, setPrivateKeyError] = useState("");
  const ppkFileInputRef = useRef<HTMLInputElement | null>(null);
  const theme = useTheme();
  console.log(vendorName, 'vendorName in cred form');
  const isDarkMode = theme.palette.mode === "dark";

  const handlePpkUploadClick = () => {
    ppkFileInputRef.current?.click();
  };

  const handlePpkFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;

    if (!file) {
      setPrivateKeyFileName("");
      setPrivateKeyError("");
      onPrivateKeyUpload?.(null);
      return;
    }

    const fileName = file.name.toLowerCase();

    if (!fileName.endsWith(".pem")) {
      setPrivateKeyFileName("");
      setPrivateKeyError("Please upload .pem file.");
      if (ppkFileInputRef.current) {
        ppkFileInputRef.current.value = "";
      }
      onPrivateKeyUpload?.(null);
      return;
    }

    setPrivateKeyFileName(file.name);
    setPrivateKeyError("");
    const reader = new FileReader();
    reader.onload = () => {
      const result = typeof reader.result === "string" ? reader.result : "";
      onPrivateKeyUpload?.(file, result);
    };
    reader.onerror = () => {
      setPrivateKeyError("Unable to read the selected file. Please try again.");
      if (ppkFileInputRef.current) {
        ppkFileInputRef.current.value = "";
      }
      setPrivateKeyFileName("");
      onPrivateKeyUpload?.(null);
    };
    reader.readAsText(file);
  };

  useEffect(() => {
    if (passwordType !== "private_key") {
      setPrivateKeyFileName("");
      setPrivateKeyError("");
      if (ppkFileInputRef.current) {
        ppkFileInputRef.current.value = "";
      }
      onPrivateKeyUpload?.(null);
    }
  }, [passwordType, onPrivateKeyUpload]);

  useEffect(() => {
    if (passwordType === "private_key") {
      if (privateKeyValidationMessage) {
        setPrivateKeyError(privateKeyValidationMessage);
      } else if (!privateKeyFileName) {
        setPrivateKeyError("");
      }
    }
  }, [privateKeyValidationMessage, passwordType, privateKeyFileName]);

  return (
    <>
      {selectedCredential === "Create New Credential" && (
        <Box>
          {/* Username Input Field */}
          {((component !== 'api')) && (

            <TextField
              label="Username"
              value={username}
              autoComplete="new-password"
              onChange={handleUsernameChange}
              fullWidth
              required
              sx={{ marginBottom: 2 }}
              error={!!usernameError}
              helperText={usernameError}
            />

          )

          }
          {component !== 'api' && (
            <>
              <Typography sx={{ mb: 1 }}>Password Type</Typography>

              {/* Password Type Radio Group */}
              <FormControl
                component="fieldset"
                required
                // sx={{ marginBottom: 2 }}
                error={!!passwordTypeError}
              >
                <RadioGroup
                  row
                  value={passwordType}
                  onChange={handlePasswordTypeChange}
                >
                  <FormControlLabel
                    value="password"
                    control={<Radio />}
                    label={
                      <Typography
                        sx={{
                          fontSize: "13px",
                          fontWeight: "bold",
                          color: isDarkMode ? "#BDBDDA" : "#110f27"
                        }}
                      >
                        Password
                      </Typography>
                    }
                  />

                  {component !== 'windows' && (
                    <FormControlLabel
                      value="private_key"
                      control={<Radio />}
                      label={
                        <Typography
                          sx={{
                            fontSize: "13px",
                            fontWeight: "bold",
                            color: isDarkMode ? "#BDBDDA" : "#110f27"
                          }}
                        >
                          Private Key
                        </Typography>
                      }
                    />
                  )}
                  {/* <FormControlLabel
                    value="none"
                    control={<Radio />}
                    label={
                      <Typography
                        sx={{
                          fontSize: "13px",
                          fontWeight: "bold",
                          color: isDarkMode ? "#BDBDDA" : "#110f27"
                        }}
                      >
                        None
                      </Typography>
                    }
                  /> */}
                </RadioGroup>
                <FormHelperText>{passwordTypeError}</FormHelperText>
              </FormControl>
              {passwordType === "private_key" && (
                <>
                  <Box
                    sx={{
                      border: "1px dashed #3a2ada",
                      padding: "10px",
                      textAlign: "center",
                      mb: 2,
                    }}
                  >
                    <input
                      type="file"
                      accept=".pem"
                      ref={ppkFileInputRef}
                      onChange={handlePpkFileChange}
                      style={{ display: "none" }}
                    />
                    <Button
                      variant="contained"
                      onClick={handlePpkUploadClick}
                      sx={{
                        backgroundColor: "#3A2AdA",
                        color: "white",
                        "&:hover": {
                          backgroundColor: "#3225b7",
                        },
                      }}
                    >
                      Upload PEM File
                    </Button>
                    {privateKeyFileName && (
                      <Box sx={{ mt: 1, fontSize: "12px", color: "text.secondary" }}>
                        Selected: {privateKeyFileName}
                      </Box>
                    )}
                    <Box>
                      <p>If a PPK file is available, it should be converted into a PEM file</p>
                    </Box>
                    {privateKeyError && (
                      <FormHelperText error sx={{ mt: 1 }}>
                        {privateKeyError}
                      </FormHelperText>
                    )}
                  </Box>


                  {component !== 'windows' && (
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <FormControlLabel
                        control={
                          <IOSSwitch
                            checked={execPassword}
                            onChange={handleExecPasswordToggle}
                            disabled={autoAssign}
                            sx={{ mr: 2 }}
                            offColor="#696595!important"
                            onColor="#3a2ada!important"
                          />
                        }
                        label={
                          <Typography
                            style={{
                              fontSize: "15px",
                              fontWeight: 500,
                              lineHeight: "16px",
                              color: isDarkMode ? "#BDBDDA" : "#1e2036",
                            }}
                          >
                            Exec Password
                          </Typography>
                        }
                        labelPlacement="start"
                        sx={{
                          justifyContent: "space-between",
                          width: "100%",
                          marginBottom: 2,
                        }}
                      />
                    </div>
                  )}

                  {execPassword && (
                    <TextField
                      label="Exec Password"
                      type={seePassword ? "text" : "password"}
                      value={execPasswordText}
                      autoComplete="new-password"
                      onChange={handleExecPasswordText}
                      fullWidth
                      sx={{ marginBottom: 2 }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <span className="visibility-icon">
                              {seePassword ? (
                                <Visibility onClick={() => setSeePassword(!seePassword)} />
                              ) : (
                                <VisibilityOff onClick={() => setSeePassword(!seePassword)} />
                              )}
                            </span>
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                </>
              )}


              {/* Password Input Field */}
              {(passwordType === "password") && (
                <>
                  <TextField
                    label={passwordType === "password" ? "Password" : "Private Key"}
                    value={password}
                    type={showPassword ? "text" : "password"}
                    autoComplete="new-password"
                    onChange={handlePasswordChange}
                    fullWidth
                    required
                    sx={{ marginBottom: 2 }}
                    error={!!passwordError}
                    helperText={passwordError}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <span className="visibility-icon">
                            {showPassword ? (
                              <Visibility onClick={() => setShowPassword(!showPassword)} />
                            ) : (
                              <VisibilityOff onClick={() => setShowPassword(!showPassword)} />
                            )}
                          </span>
                        </InputAdornment>
                      ),
                    }}
                  />

                  {component !== 'windows' && (
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <FormControlLabel
                        control={
                          <IOSSwitch
                            checked={execPassword}
                            onChange={handleExecPasswordToggle}
                            disabled={autoAssign}
                            sx={{ mr: 2 }}
                            offColor="#696595!important"
                            onColor="#3a2ada!important"
                          />
                        }
                        label={
                          <Typography
                            style={{
                              fontSize: "15px",
                              fontWeight: 500,
                              lineHeight: "16px",
                              color: isDarkMode ? "#BDBDDA" : "#1e2036",
                            }}
                          >
                            Exec Password
                          </Typography>
                        }
                        labelPlacement="start"
                        sx={{
                          justifyContent: "space-between",
                          width: "100%",
                          marginBottom: 2,
                        }}
                      />
                      <Typography
                        style={{
                          fontSize: "15px",
                          fontWeight: 500,
                          lineHeight: "16px",
                          color: isDarkMode ? "#BDBDDA" : "#1e2036",
                          marginLeft: "20px",
                          marginBottom: "10px",
                        }}
                      >
                        Auto Assign
                      </Typography>

                      {/* Auto Assign Switch */}
                      <FormControlLabel
                        control={
                          <IOSSwitch
                            checked={autoAssign}
                            onChange={handleAutoAssignToggle}
                            disabled={execPassword}
                            sx={{ mr: 2 }}
                            offColor="#696595!important"
                            onColor="#3a2ada!important"
                          />
                        }
                        label={
                          <Typography
                            style={{
                              fontWeight: 400,
                              fontSize: "12px",
                              maxWidth: "350px",
                              lineHeight: "15px",
                            }}
                          >
                            This will auto-assign Login Password as Exec Password when
                            missing.{" "}
                          </Typography>
                        }
                        labelPlacement="start"
                        sx={{
                          marginBottom: 2,
                          justifyContent: "space-between",
                          width: "100%",
                        }}
                      />
                    </div>
                  )}
                  {execPassword && (
                    <TextField
                      label="Exec Password"
                      type={seePassword ? "text" : "password"}
                      value={execPasswordText}
                      autoComplete="new-password"
                      onChange={handleExecPasswordText}
                      fullWidth
                      required
                      sx={{ marginBottom: 2 }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <span className="visibility-icon">
                              {seePassword ? (
                                <Visibility onClick={() => setSeePassword(!seePassword)} />
                              ) : (
                                <VisibilityOff onClick={() => setSeePassword(!seePassword)} />
                              )}
                            </span>
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                </>


              )}

              {/* Exec Password Switch */}


              {/* Exec Password Text Field */}

            </>

          )

          }

          {component == 'api' && (
            <>
              <Typography sx={{ mb: 1 }}>Password Type</Typography>

              {/* Password Type Radio Group */}
              <FormControl
                component="fieldset"
                required
                // sx={{ marginBottom: 2 }}
                error={!!passwordTypeError}
              >
                <RadioGroup
                  row
                  value={passwordType}
                  onChange={handlePasswordTypeChange}
                >
                  <FormControlLabel
                    value="password"
                    control={<Radio />}
                    label={
                      <Typography
                        sx={{
                          fontSize: "13px",
                          fontWeight: "bold",
                          color: isDarkMode ? "#BDBDDA" : "#110f27"
                        }}
                      >
                        Password
                      </Typography>
                    }
                  />
                  <FormControlLabel
                    value="api_token"
                    control={<Radio />}
                    label={
                      <Typography
                        sx={{
                          fontSize: "13px",
                          fontWeight: "bold",
                          color: isDarkMode ? "#BDBDDA" : "#110f27"
                        }}
                      >
                        API Token
                      </Typography>
                    }
                  />

                </RadioGroup>
                <FormHelperText>{passwordTypeError}</FormHelperText>
              </FormControl>

              {/* Password Input Field */}
              {(passwordType === "password") && (
                <>
                  <TextField
                    label="Username"
                    value={username}
                    autoComplete="new-password"
                    onChange={handleUsernameChange}
                    fullWidth
                    required
                    sx={{ marginBottom: 2 }}
                    error={!!usernameError}
                    helperText={usernameError}
                  />
                  <TextField
                    label={passwordType === "password" ? "Password" : "Private Key"}
                    value={password}
                    type={showPassword ? "text" : "password"}
                    autoComplete="new-password"
                    onChange={handlePasswordChange}
                    fullWidth
                    required
                    sx={{ marginBottom: 2 }}
                    error={!!passwordError}
                    helperText={passwordError}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <span className="visibility-icon">
                            {showPassword ? (
                              <Visibility onClick={() => setShowPassword(!showPassword)} />
                            ) : (
                              <VisibilityOff onClick={() => setShowPassword(!showPassword)} />
                            )}
                          </span>
                        </InputAdornment>
                      ),
                    }}
                  />


                </>


              )}

              {(passwordType === "api_token") && (<>
                <TextField
                  label="API Token"
                  value={password}
                  autoComplete="new-password"
                  onChange={handlePasswordChange}
                  fullWidth
                  type="text"
                  required
                  sx={{ marginBottom: 2 }}
                  error={!!passwordError}
                  helperText={passwordError}
                />


              </>)}

              {/* Exec Password Switch */}


              {/* Exec Password Text Field */}

            </>

          )

          }





          {/* Save Credential Checkbox */}
          <FormControlLabel
            control={
              <Checkbox
                checked={saveCredential}
                onChange={handleSaveCredential}
                disabled={isScheduled}
              />
            }
            label="Save Credential"
            sx={{
              ".MuiFormControlLabel-label": {
                fontSize: "14px",
                color: "#696595!important",
                lineHeight: "24px",
                fontWeight: 500,
              },
            }}
          />

          {/* Credential Name Input Field */}
          <TextField
            label="Credential Name"
            value={credName}
            onChange={handleCredName}
            fullWidth
            required
            sx={{ marginBottom: 2 }}
            error={!!credNameError}
            helperText={credNameError}
          />
        </Box >
      )}
    </>
  );
};

export default CredentialForm;
